import { FormEvent, useState, useEffect } from "react";
import { Auth } from "aws-amplify";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Input, Label, Card, CardContent, CardHeader } from "@hotelise/ui";
import { Home, Lock, KeyRound, ArrowRight, Eye, EyeOff, Sun, Moon } from "lucide-react";

const ResetPassword = (): JSX.Element => {
	const [code, setCode] = useState("");
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [isDarkMode, setIsDarkMode] = useState(false);
	const navigate = useNavigate();
	const location = useLocation();
	const searchParams = new URLSearchParams(location.search);
	const email = (location.state as { email?: string })?.email || searchParams.get("email") || undefined;

	useEffect(() => {
		const stored = localStorage.getItem("theme");
		const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
		const dark = stored === "dark" || (!stored && prefersDark);
		setIsDarkMode(dark);
		document.documentElement.classList.toggle("dark", dark);
	}, []);

	const toggleTheme = () => {
		const next = !isDarkMode;
		setIsDarkMode(next);
		document.documentElement.classList.toggle("dark", next);
		localStorage.setItem("theme", next ? "dark" : "light");
	};

	const getErrorMessage = (error: any): string => {
		const type = error?.code || error?.name || error?.__type;
		switch (type) {
			case "CodeMismatchException":
				return "Invalid verification code. Please try again.";
			case "ExpiredCodeException":
				return "Verification code has expired. Please request a new one.";
			case "LimitExceededException":
				return "Too many attempts. Please try again later.";
			case "InvalidPasswordException":
				return error?.message || "Password does not meet requirements.";
			default:
				return error?.message || "Something went wrong. Please try again.";
		}
	};

	const handleSubmit = async (event: FormEvent) => {
		event.preventDefault();
		setErrorMessage("");

		if (!email) {
			setErrorMessage("Email not found. Please go back and try again.");
			return;
		}

		const normalizedEmail = email.trim().toLowerCase();

		try {
			setIsLoading(true);
			await Auth.forgotPasswordSubmit(normalizedEmail, code, password);
			await Auth.signIn(normalizedEmail, password);
		} catch (error: any) {
			setErrorMessage(getErrorMessage(error));
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="min-h-screen w-full flex flex-col items-center justify-center bg-background relative p-4 sm:p-6">
			{/* Theme toggle */}
			<Button
				variant="ghost"
				size="icon"
				onClick={toggleTheme}
				className="absolute top-4 right-4 z-20"
				type="button"
				aria-label="Toggle theme"
				title="Toggle theme"
			>
				{isDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
			</Button>

			{/* Background pattern */}
			<div
				className="fixed inset-0 opacity-[0.015] dark:opacity-[0.03] pointer-events-none"
				style={{
					backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)`,
					backgroundSize: "40px 40px",
				}}
			/>

			<div className="relative z-10 w-full max-w-md space-y-6">
				{/* Branding */}
				<div className="flex justify-center">
					<div className="inline-flex items-center gap-3">
						<div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center shadow-lg shadow-primary/25">
							<Home className="h-6 w-6 text-primary-foreground" />
						</div>
						<h1 className="text-2xl font-bold tracking-tight">Hotelise</h1>
					</div>
				</div>

				{/* Card */}
				<Card className="border shadow-xl shadow-black/5 dark:shadow-black/20">
					<CardHeader className="text-center pb-2 pt-6">
						<h2 className="text-lg font-semibold">Reset your password</h2>
						<p className="text-sm text-muted-foreground">
							{email
								? <>Enter the code sent to <span className="font-medium text-foreground">{email}</span></>
								: "Enter the code and your new password"}
						</p>
					</CardHeader>
					<CardContent className="pb-6 px-5 sm:px-6">
						<form className="space-y-4" onSubmit={handleSubmit}>
							<div className="space-y-1.5">
								<Label htmlFor="code" className="text-xs">Code</Label>
								<div className="relative">
									<KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
									<Input
										id="code"
										type="text"
										placeholder="Enter reset code"
										className="pl-10 h-10"
										value={code}
										onChange={(e) => { setErrorMessage(""); setCode(e.target.value); }}
									/>
								</div>
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="password" className="text-xs">New Password</Label>
								<div className="relative">
									<Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
									<Input
										id="password"
										type={showPassword ? "text" : "password"}
										placeholder="••••••••"
										className="pl-10 pr-10 h-10"
										value={password}
										onChange={(e) => { setErrorMessage(""); setPassword(e.target.value); }}
									/>
									<Button
										type="button"
										variant="ghost"
										size="icon"
										className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:text-foreground"
										onClick={() => setShowPassword(!showPassword)}
									aria-label={showPassword ? "Hide password" : "Show password"}
									title={showPassword ? "Hide password" : "Show password"}
									>
										{showPassword ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
									</Button>
								</div>
							</div>

							{errorMessage && (
								<div className="p-3 rounded-lg border border-destructive/50 bg-destructive/10 text-sm text-destructive" role="alert">
									{errorMessage}
								</div>
							)}

							<Button type="submit" className="w-full h-10 group" disabled={isLoading}>
								{isLoading ? "Resetting…" : "Reset Password"}
								<ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-1" />
							</Button>
						</form>
					</CardContent>
				</Card>

				{/* Back link */}
				<p className="text-center text-sm text-muted-foreground">
					<Button
						variant="link"
						className="px-1 h-auto text-sm"
						onClick={() => navigate("/")}
						type="button"
					>
						Back to Sign In
					</Button>
				</p>

				{/* Footer */}
				<p className="text-center text-[10px] text-muted-foreground/60 pt-2">
					© 2026 Hotelise. All rights reserved.
				</p>
			</div>
		</div>
	);
};

export default ResetPassword;
